/* ============================================================
   storage — the right-click menu.

   One component for every surface that has one: a row, the empty
   space below the rows, a folder in the tree. The caller passes
   a point and a list of items; this owns the rest — flipping so
   the menu never opens off-screen, closing on anything that
   would move it (scroll, resize, another click, Escape), and
   keyboard navigation for people who opened it with the context
   key rather than the mouse.

   Items are `{ label, icon, onSelect, hint, disabled, danger }`
   or the string 'sep'.
   ============================================================ */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './lib';

const MARGIN = 8;

export default function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y, ready: false });

  // Measure, then place. Rendering at the click point first and correcting
  // afterwards would show the menu jumping on every open near an edge.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = x;
    let top = y;
    if (left + width > vw - MARGIN) left = Math.max(MARGIN, x - width);
    if (top + height > vh - MARGIN) top = Math.max(MARGIN, y - height);
    // Taller than the viewport (a long menu on a short phone): pin it to the
    // top and let it scroll rather than hanging off the bottom.
    if (height > vh - MARGIN * 2) top = MARGIN;
    setPos({ left, top, ready: true });
  }, [x, y, items]);

  useEffect(() => {
    const first = ref.current?.querySelector('.st-ctx-item:not(:disabled)');
    first?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const close = () => onClose();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const opts = [...(ref.current?.querySelectorAll('.st-ctx-item:not(:disabled)') || [])];
      if (!opts.length) return;
      const at = opts.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown'
        ? (at + 1) % opts.length
        : (at - 1 + opts.length) % opts.length;
      opts[next]?.focus();
    };
    const onDown = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    // `true` — a scroll inside the file list does not bubble, and a menu that
    // stays put while the rows move under it is pointing at the wrong thing.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('blur', close);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('blur', close);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="st-ctx"
      role="menu"
      style={{ left: pos.left, top: pos.top, visibility: pos.ready ? 'visible' : 'hidden' }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((it, i) => (
        it === 'sep' ? <div key={`sep${i}`} className="st-ctx-sep" role="separator"/> : (
          <button
            key={it.label}
            type="button"
            role="menuitem"
            className={`st-ctx-item ${it.danger ? 'is-danger' : ''}`}
            disabled={it.disabled}
            onClick={() => { onClose(); it.onSelect(); }}
          >
            <Icon name={it.icon || 'file'} size={14}/>
            <span className="st-ctx-label">{it.label}</span>
            {it.hint && <span className="st-ctx-hint">{it.hint}</span>}
          </button>
        )
      ))}
    </div>
  );
}
